import { db } from "./dbClient";

// ─── TravelRulePage.jsx ───────────────────────────────────────────────────────
// Travel Rule (FATF Rec. 16) compliance management
// Features: Transaction log, VASP directory, threshold monitor,
//           unhosted wallet workflow, case files

import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";

// ─── STATIC DATA ─────────────────────────────────────────────────────────────

const JURISDICTION_THRESHOLDS = [
  { code: "FATF", name: "FATF (Global Standard)",    currency: "USD", symbol: "$",   threshold: 1000   },
  { code: "US",   name: "United States (FinCEN)",    currency: "USD", symbol: "$",   threshold: 3000   },
  { code: "EU",   name: "European Union (MiCA/TFR)", currency: "EUR", symbol: "€",   threshold: 0      },
  { code: "GB",   name: "United Kingdom (FCA)",      currency: "GBP", symbol: "£",   threshold: 1000   },
  { code: "CA",   name: "Canada (FINTRAC)",          currency: "CAD", symbol: "C$",  threshold: 1000   },
  { code: "SG",   name: "Singapore (MAS)",           currency: "SGD", symbol: "S$",  threshold: 1500   },
  { code: "AU",   name: "Australia (AUSTRAC)",       currency: "AUD", symbol: "A$",  threshold: 1000   },
  { code: "CH",   name: "Switzerland (FINMA)",       currency: "CHF", symbol: "CHF", threshold: 1000   },
  { code: "JP",   name: "Japan (FSA)",               currency: "JPY", symbol: "¥",   threshold: 100000 },
  { code: "HK",   name: "Hong Kong (HKMA/SFC)",     currency: "HKD", symbol: "HK$", threshold: 8000   },
  { code: "BR",   name: "Brazil (BCB/COAF)",         currency: "BRL", symbol: "R$",  threshold: 1000   },
];

const SEED_VASPS = [
  { id: "v1", name: "Coinbase",    jurisdiction: "US", regNo: "NMLS #1255579",  protocol: "TRISA",        status: "compliant",     website: "coinbase.com",   contact: "compliance@coinbase.com",  notes: "",                                            addresses: [] },
  { id: "v2", name: "Binance",     jurisdiction: "EU", regNo: "W049061",        protocol: "Notabene",     status: "compliant",     website: "binance.com",    contact: "compliance@binance.com",   notes: "",                                            addresses: [] },
  { id: "v3", name: "Kraken",      jurisdiction: "US", regNo: "NMLS #1434806",  protocol: "TRP",          status: "compliant",     website: "kraken.com",     contact: "compliance@kraken.com",    notes: "",                                            addresses: [] },
  { id: "v4", name: "Bitstamp",    jurisdiction: "EU", regNo: "VASP-EU-001",    protocol: "Notabene",     status: "compliant",     website: "bitstamp.net",   contact: "compliance@bitstamp.net",  notes: "",                                            addresses: [] },
  { id: "v5", name: "OKX",         jurisdiction: "SG", regNo: "PS20200079",     protocol: "Unknown",      status: "unknown",       website: "okx.com",        contact: "",                         notes: "Travel Rule protocol not confirmed",          addresses: [] },
  { id: "v6", name: "Bybit",       jurisdiction: "AU", regNo: "DCE100812167",   protocol: "Unknown",      status: "non_compliant", website: "bybit.com",      contact: "",                         notes: "Not confirmed TR compliant in all jurisdictions", addresses: [] },
  { id: "v7", name: "Gemini",      jurisdiction: "US", regNo: "NMLS #1237927",  protocol: "TRISA",        status: "compliant",     website: "gemini.com",     contact: "compliance@gemini.com",    notes: "",                                            addresses: [] },
  { id: "v8", name: "Crypto.com",  jurisdiction: "SG", regNo: "PS20200076",     protocol: "Notabene",     status: "compliant",     website: "crypto.com",     contact: "compliance@crypto.com",    notes: "",                                            addresses: [] },
];

const PROTOCOLS = ["TRISA", "TRP", "Notabene", "Sygna Bridge", "VerifyVASP", "OpenVASP", "Unknown"];
const ASSETS    = ["BTC", "ETH", "USDT", "USDC", "BNB", "SOL", "XRP", "MATIC", "ADA", "LTC", "Other"];

function uid() { return `tr_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`; }

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-CA");
}

function truncate(str, n = 16) {
  if (!str) return "—";
  return str.length > n ? `${str.slice(0, 8)}…${str.slice(-6)}` : str;
}

// ─── SHARED SMALL COMPONENTS ──────────────────────────────────────────────────

function Btn({ children, onClick, variant = "primary", disabled = false, small = false, style: ex = {} }) {
  const C = useTheme();
  const base = {
    padding: small ? "5px 10px" : "8px 16px",
    borderRadius: 7, cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "'DM Mono',monospace", fontSize: small ? 11 : 12,
    fontWeight: 600, border: "none", transition: "opacity 0.15s",
    opacity: disabled ? 0.45 : 1, ...ex,
  };
  const styles = {
    primary: { background: C.accent,   color: C.bg },
    danger:  { background: C.danger,   color: "#fff" },
    ghost:   { background: "transparent", border: `1px solid ${C.border}`, color: C.textMid },
    subtle:  { background: C.accentGlow, border: `1px solid ${C.borderLight}`, color: C.accent },
  };
  return <button style={{ ...base, ...styles[variant] }} onClick={disabled ? undefined : onClick}>{children}</button>;
}

function Badge({ label, color }) {
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10,
      background: `${color}22`, border: `1px solid ${color}55`,
      color, fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

function Modal({ title, onClose, children, width = 560 }) {
  const C = useTheme();
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
        width: "100%", maxWidth: width, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none",
            color: C.textDim, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: "20px 22px", overflowY: "auto", flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  const C = useTheme();
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ color: C.textDim, fontSize: 11, letterSpacing: "0.08em", marginBottom: 5 }}>{label}</div>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = "text", ...rest }) {
  const C = useTheme();
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{ width: "100%", background: "rgba(0,0,0,0.2)", border: `1px solid ${C.border}`,
        borderRadius: 7, color: C.text, fontSize: 12, padding: "8px 11px",
        fontFamily: "'DM Mono',monospace", outline: "none", boxSizing: "border-box" }}
      {...rest} />
  );
}

function Select({ value, onChange, children, ...rest }) {
  const C = useTheme();
  return (
    <select value={value} onChange={onChange}
      style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 7, color: C.text, fontSize: 12, padding: "8px 11px",
        fontFamily: "'DM Mono',monospace", outline: "none", cursor: "pointer" }}
      {...rest}>
      {children}
    </select>
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }) {
  const C = useTheme();
  return (
    <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
      style={{ width: "100%", background: "rgba(0,0,0,0.2)", border: `1px solid ${C.border}`,
        borderRadius: 7, color: C.text, fontSize: 12, padding: "8px 11px",
        fontFamily: "'DM Mono',monospace", outline: "none", resize: "vertical",
        boxSizing: "border-box" }} />
  );
}

// ─── TAB 1: TRANSACTION LOG ───────────────────────────────────────────────────

const TR_STATUS_META = {
  compliant:  { label: "Compliant",   color: "#00E676" },
  pending:    { label: "Pending",     color: "#FF8C00" },
  needs_info: { label: "Needs Info",  color: "#FF4444" },
  exempt:     { label: "Exempt",      color: "#8A9BB5" },
};

function getTranslatedTRStatus(status, t) {
  const statusMap = {
    "compliant": "travelRule.statuses.compliant",
    "pending": "travelRule.statuses.pending",
    "needs_info": "travelRule.statuses.needs_info",
    "exempt": "travelRule.statuses.exempt",
  };
  return t(statusMap[status] || status);
}

function AddTransactionModal({ onClose, onSave, thresholds }) {
  const C = useTheme();
  const { t } = useTranslation();
  const empty = {
    date: new Date().toISOString().slice(0, 10),
    txHash: "", asset: "BTC", amountCrypto: "", amountUSD: "",
    direction: "outgoing",
    originatorName: "", originatorAccount: "", originatorVASP: "",
    beneficiaryName: "", beneficiaryAccount: "", beneficiaryVASP: "",
    isUnhosted: false, jurisdiction: "FATF", trStatus: "pending", notes: "",
  };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const jur    = thresholds.find(t => t.code === form.jurisdiction) || thresholds[0];
  const amt    = parseFloat(form.amountUSD) || 0;
  const breached = jur && (jur.threshold === 0 || amt >= jur.threshold);

  function save() {
    if (!form.originatorName || !form.beneficiaryName || !form.amountUSD) return;
    // #20 Travel Rule enforcement: if threshold breached, require VASP info — auto-set needs_info
    let finalStatus = form.trStatus;
    if (breached) {
      const missingVASP = !form.originatorVASP.trim() || !form.beneficiaryVASP.trim();
      const missingAccount = !form.originatorAccount.trim() || !form.beneficiaryAccount.trim();
      if (missingVASP || missingAccount) {
        finalStatus = "needs_info"; // enforce: can't be compliant without full VASP data
      }
    }
    onSave({ ...form, trStatus: finalStatus, id: uid(), createdAt: new Date().toISOString(), thresholdBreached: breached });
    onClose();
  }

  return (
    <Modal title={t('travelRule.addTransaction')} onClose={onClose} width={620}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Field label={t('travelRule.date')}><Input type="date" value={form.date} onChange={e => set("date", e.target.value)} /></Field>
        <Field label={t('travelRule.direction')}>
          <Select value={form.direction} onChange={e => set("direction", e.target.value)}>
            <option value="outgoing">{t('travelRule.outgoing')}</option>
            <option value="incoming">{t('travelRule.incoming')}</option>
          </Select>
        </Field>
        <Field label={t('travelRule.asset')}>
          <Select value={form.asset} onChange={e => set("asset", e.target.value)}>
            {ASSETS.map(a => <option key={a}>{a}</option>)}
          </Select>
        </Field>
        <Field label={t('travelRule.amountCrypto')}><Input value={form.amountCrypto} onChange={e => set("amountCrypto", e.target.value)} placeholder="e.g. 0.45" /></Field>
        <Field label={t('travelRule.amountUSD')}><Input value={form.amountUSD} onChange={e => set("amountUSD", e.target.value)} placeholder="e.g. 14500" /></Field>
        <Field label={t('travelRule.jurisdiction')}>
          <Select value={form.jurisdiction} onChange={e => set("jurisdiction", e.target.value)}>
            {thresholds.map(j => <option key={j.code} value={j.code}>{j.name}</option>)}
          </Select>
        </Field>
      </div>

      {breached && (
        <div style={{ background: "rgba(255,140,0,0.1)", border: "1px solid rgba(255,140,0,0.3)",
          borderRadius: 7, padding: "10px 12px", marginBottom: 14, fontSize: 12, color: "#FF8C00" }}>
          {t('travelRule.thresholdWarning', { name: jur.name, symbol: jur.symbol, threshold: jur.threshold === 0 ? `0 — ${t('travelRule.allTransfers')}` : jur.threshold.toLocaleString() })}
          {(!form.originatorVASP.trim() || !form.beneficiaryVASP.trim() || !form.originatorAccount.trim() || !form.beneficiaryAccount.trim()) && (
            <div style={{ marginTop: 6, color: "#EF4444", fontWeight: 700 }}>
              ⚠ Travel Rule requires originator + beneficiary VASP name and account/address. Missing fields will force status to "Needs Info".
            </div>
          )}
        </div>
      )}

      <Field label={t('travelRule.txHashId')}><Input value={form.txHash} onChange={e => set("txHash", e.target.value)} placeholder="0x… or blockchain TX ID" /></Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px", marginTop: 4 }}>
        <div>
          <div style={{ color: C.accent, fontSize: 11, letterSpacing: "0.08em", marginBottom: 8, fontWeight: 700 }}>{t('travelRule.originator')}</div>
          <Field label={t('travelRule.fullName')}><Input value={form.originatorName} onChange={e => set("originatorName", e.target.value)} placeholder={t('travelRule.legalNamePlaceholder')} /></Field>
          <Field label={t('travelRule.accountAddress')}><Input value={form.originatorAccount} onChange={e => set("originatorAccount", e.target.value)} placeholder={t('travelRule.walletOrAccountPlaceholder')} /></Field>
          <Field label={t('travelRule.vaspName')}><Input value={form.originatorVASP} onChange={e => set("originatorVASP", e.target.value)} placeholder={t('travelRule.exchangeOrInstitution')} /></Field>
        </div>
        <div>
          <div style={{ color: C.purple, fontSize: 11, letterSpacing: "0.08em", marginBottom: 8, fontWeight: 700 }}>{t('travelRule.beneficiary')}</div>
          <Field label={t('travelRule.fullName')}><Input value={form.beneficiaryName} onChange={e => set("beneficiaryName", e.target.value)} placeholder={t('travelRule.legalNamePlaceholder')} /></Field>
          <Field label={t('travelRule.accountAddress')}><Input value={form.beneficiaryAccount} onChange={e => set("beneficiaryAccount", e.target.value)} placeholder={t('travelRule.walletOrAccountPlaceholder')} /></Field>
          <Field label={t('travelRule.vaspName')}><Input value={form.beneficiaryVASP} onChange={e => set("beneficiaryVASP", e.target.value)} placeholder={t('travelRule.beneficiaryVaspPlaceholder')} /></Field>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, marginTop: 4 }}>
        <input type="checkbox" id="unhosted" checked={form.isUnhosted} onChange={e => set("isUnhosted", e.target.checked)} />
        <label htmlFor="unhosted" style={{ color: C.textMid, fontSize: 12, cursor: "pointer" }}>
          {t('travelRule.unhostedCheckbox')}
        </label>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Field label={t('travelRule.travelRuleStatus')}>
          <Select value={form.trStatus} onChange={e => set("trStatus", e.target.value)}>
            {Object.entries(TR_STATUS_META).map(([k]) => <option key={k} value={k}>{getTranslatedTRStatus(k, t)}</option>)}
          </Select>
        </Field>
      </div>

      <Field label={t('common.notes')}><Textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder={t('travelRule.notesPlaceholder')} /></Field>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
        <Btn variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
        <Btn onClick={save} disabled={!form.originatorName || !form.beneficiaryName || !form.amountUSD}>{t('travelRule.saveTransaction')}</Btn>
      </div>
    </Modal>
  );
}

function TransactionLog({ transactions, setTransactions, thresholds }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [showAdd, setShowAdd]   = useState(false);
  const [search, setSearch]     = useState("");
  const [filter, setFilter]     = useState("all");
  const [selected, setSelected] = useState(null);

  function addTx(tx) { setTransactions(p => [tx, ...p]); }
  function updateStatus(id, trStatus) {
    setTransactions(p => p.map(t => t.id === id ? { ...t, trStatus } : t));
    if (selected?.id === id) setSelected(prev => ({ ...prev, trStatus }));
  }
  function deleteTx(id) { setTransactions(p => p.filter(t => t.id !== id)); setSelected(null); }

  const filtered = useMemo(() => transactions.filter(tx => {
    const q = search.toLowerCase();
    const matchSearch = !q || tx.originatorName.toLowerCase().includes(q)
      || tx.beneficiaryName.toLowerCase().includes(q)
      || (tx.txHash || "").toLowerCase().includes(q);
    const matchFilter = filter === "all" || tx.trStatus === filter
      || (filter === "breached" && tx.thresholdBreached)
      || (filter === "unhosted" && tx.isUnhosted);
    return matchSearch && matchFilter;
  }), [transactions, search, filter]);

  return (
    <div style={{ display: "flex", gap: 20 }}>
      {/* List */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('travelRule.searchPlaceholder')} />
          <Select value={filter} onChange={e => setFilter(e.target.value)} style={{ width: 160 }}>
            <option value="all">{t('common.all')}</option>
            <option value="breached">{t('travelRule.filterThresholdBreached')}</option>
            <option value="unhosted">{t('travelRule.filterUnhosted')}</option>
            <option value="pending">{t('travelRule.filterPending')}</option>
            <option value="needs_info">{t('travelRule.filterNeedsInfo')}</option>
            <option value="compliant">{t('travelRule.filterCompliant')}</option>
            <option value="exempt">{t('travelRule.filterExempt')}</option>
          </Select>
          <Btn onClick={() => setShowAdd(true)}>{t('travelRule.addTransactionBtn')}</Btn>
        </div>

        {filtered.length === 0 && (
          <div style={{ padding: "40px 20px", textAlign: "center", color: C.textDim, fontSize: 13 }}>
            {t('travelRule.noTransactions')}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {filtered.map(tx => {
            const meta  = TR_STATUS_META[tx.trStatus] || TR_STATUS_META.pending;
            const isSel = selected?.id === tx.id;
            return (
              <div key={tx.id}
                onClick={() => setSelected(isSel ? null : tx)}
                style={{ background: isSel ? C.accentGlow : C.surface,
                  border: `1px solid ${isSel ? C.accent : C.border}`,
                  borderRadius: 8, padding: "12px 14px", cursor: "pointer",
                  transition: "all 0.12s" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ color: C.textDim, fontSize: 11 }}>{fmtDate(tx.date)}</span>
                  <span style={{ color: tx.direction === "outgoing" ? C.danger : C.success, fontSize: 11, fontWeight: 700 }}>
                    {tx.direction === "outgoing" ? "↑ OUT" : "↓ IN"}
                  </span>
                  <span style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>
                    {tx.amountCrypto} {tx.asset}
                  </span>
                  <span style={{ color: C.textDim, fontSize: 11 }}>${parseFloat(tx.amountUSD || 0).toLocaleString()}</span>
                  <span style={{ flex: 1, color: C.textMid, fontSize: 12 }}>
                    {tx.originatorName} → {tx.beneficiaryName}
                  </span>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    {tx.thresholdBreached && <Badge label={t('travelRule.badgeThreshold')} color="#FF8C00" />}
                    {tx.isUnhosted && <Badge label={t('travelRule.badgeUnhosted')} color="#A78BFA" />}
                    <Badge label={getTranslatedTRStatus(tx.trStatus, t)} color={meta.color} />
                  </div>
                </div>
                {tx.txHash && (
                  <div style={{ color: C.textDim, fontSize: 10, marginTop: 5, fontFamily: "'DM Mono',monospace" }}>
                    {t('travelRule.txLabel')}: {truncate(tx.txHash, 40)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{ width: 300, flexShrink: 0, background: C.surface,
          border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 18px",
          alignSelf: "flex-start", position: "sticky", top: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{t('travelRule.transactionDetail')}</div>
            <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 16 }}>×</button>
          </div>

          <DetailRow label={t('travelRule.date')}        value={fmtDate(selected.date)} C={C} />
          <DetailRow label={t('travelRule.direction')}   value={selected.direction === "outgoing" ? `↑ ${t('travelRule.outgoingShort')}` : `↓ ${t('travelRule.incomingShort')}`} C={C} />
          <DetailRow label={t('travelRule.asset')}       value={`${selected.amountCrypto} ${selected.asset}`} C={C} />
          <DetailRow label={t('travelRule.usdValue')}    value={`$${parseFloat(selected.amountUSD || 0).toLocaleString()}`} C={C} />
          <DetailRow label={t('travelRule.txHash')}      value={truncate(selected.txHash, 24)} C={C} />
          <DetailRow label={t('travelRule.jurisdiction')} value={selected.jurisdiction} C={C} />

          <div style={{ borderTop: `1px solid ${C.border}`, margin: "12px 0" }} />
          <div style={{ color: C.accent, fontSize: 10, letterSpacing: "0.08em", marginBottom: 8, fontWeight: 700 }}>{t('travelRule.originator')}</div>
          <DetailRow label={t('common.name')}    value={selected.originatorName} C={C} />
          <DetailRow label={t('travelRule.account')} value={truncate(selected.originatorAccount, 22)} C={C} />
          <DetailRow label={t('travelRule.vasp')}    value={selected.originatorVASP || "—"} C={C} />

          <div style={{ borderTop: `1px solid ${C.border}`, margin: "12px 0" }} />
          <div style={{ color: C.purple, fontSize: 10, letterSpacing: "0.08em", marginBottom: 8, fontWeight: 700 }}>{t('travelRule.beneficiary')}</div>
          <DetailRow label={t('common.name')}    value={selected.beneficiaryName} C={C} />
          <DetailRow label={t('travelRule.account')} value={truncate(selected.beneficiaryAccount, 22)} C={C} />
          <DetailRow label={t('travelRule.vasp')}    value={selected.beneficiaryVASP || "—"} C={C} />
          {selected.isUnhosted && <div style={{ color: C.purple, fontSize: 11, marginTop: 4 }}>⚠ {t('travelRule.unhostedWallet')}</div>}

          <div style={{ borderTop: `1px solid ${C.border}`, margin: "12px 0" }} />
          <div style={{ marginBottom: 10 }}>
            <div style={{ color: C.textDim, fontSize: 11, marginBottom: 5 }}>{t('travelRule.travelRuleStatus')}</div>
            <Select value={selected.trStatus}
              onChange={e => updateStatus(selected.id, e.target.value)}>
              {Object.entries(TR_STATUS_META).map(([k]) => <option key={k} value={k}>{getTranslatedTRStatus(k, t)}</option>)}
            </Select>
          </div>

          {selected.notes && (
            <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6, marginBottom: 12 }}>{selected.notes}</div>
          )}

          {/* #20: Generate Travel Rule compliance packet */}
          <Btn small onClick={() => {
            const packet = {
              schema: "FATF Travel Rule v1.0",
              generated_at: new Date().toISOString(),
              transaction: {
                id: selected.id,
                date: selected.date,
                tx_hash: selected.txHash,
                asset: selected.asset,
                amount_crypto: selected.amountCrypto,
                amount_usd: selected.amountUSD,
                direction: selected.direction,
                jurisdiction: selected.jurisdiction,
                threshold_breached: selected.thresholdBreached,
              },
              originator: {
                full_name: selected.originatorName,
                account_address: selected.originatorAccount,
                vasp_name: selected.originatorVASP,
              },
              beneficiary: {
                full_name: selected.beneficiaryName,
                account_address: selected.beneficiaryAccount,
                vasp_name: selected.beneficiaryVASP,
              },
              is_unhosted_wallet: selected.isUnhosted,
              travel_rule_status: selected.trStatus,
              notes: selected.notes,
            };
            const blob = new Blob([JSON.stringify(packet, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `TR_packet_${(selected.originatorName || "tx").replace(/\s+/g,"_")}_${selected.date || Date.now()}.json`;
            a.click();
            URL.revokeObjectURL(url);
          }} style={{ width: "100%", marginBottom: 8 }}>
            ↓ Export TR Packet (JSON)
          </Btn>
          <Btn variant="danger" small onClick={() => deleteTx(selected.id)} style={{ width: "100%" }}>{t('travelRule.deleteRecord')}</Btn>
        </div>
      )}

      {showAdd && <AddTransactionModal onClose={() => setShowAdd(false)} onSave={addTx} thresholds={thresholds} />}
    </div>
  );
}

function DetailRow({ label, value, C }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
      <span style={{ color: C.textDim, fontSize: 11 }}>{label}</span>
      <span style={{ color: C.textMid, fontSize: 11, maxWidth: 160, textAlign: "right", wordBreak: "break-all" }}>{value || "—"}</span>
    </div>
  );
}

// ─── TAB 2: VASP DIRECTORY ────────────────────────────────────────────────────

const VASP_STATUS_META = {
  compliant:     { label: "Compliant",     color: "#00E676" },
  non_compliant: { label: "Non-Compliant", color: "#FF4444" },
  unknown:       { label: "Unknown",       color: "#FF8C00" },
};

function getTranslatedVASPStatus(status, t) {
  const statusMap = {
    "compliant": "travelRule.vaspStatuses.compliant",
    "non_compliant": "travelRule.vaspStatuses.non_compliant",
    "unknown": "travelRule.vaspStatuses.unknown",
  };
  return t(statusMap[status] || status);
}

function VASPModal({ vasp, onClose, onSave }) {
  const C = useTheme();
  const { t } = useTranslation();
  const empty = { name: "", jurisdiction: "US", regNo: "", protocol: "Unknown",
    status: "unknown", website: "", contact: "", notes: "", addresses: [] };
  const [form, setForm] = useState(vasp ? { ...vasp } : empty);
  const [addrInput, setAddrInput] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function addAddr() {
    if (!addrInput.trim()) return;
    setForm(f => ({ ...f, addresses: [...(f.addresses || []), addrInput.trim()] }));
    setAddrInput("");
  }
  function removeAddr(i) { setForm(f => ({ ...f, addresses: f.addresses.filter((_, idx) => idx !== i) })); }
  function save() {
    if (!form.name) return;
    onSave({ ...form, id: vasp?.id || uid() });
    onClose();
  }

  return (
    <Modal title={vasp ? t('travelRule.editVasp') : t('travelRule.addVasp')} onClose={onClose}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Field label={t('travelRule.vaspName')}><Input value={form.name} onChange={e => set("name", e.target.value)} placeholder={t('travelRule.vaspNamePlaceholder')} /></Field>
        <Field label={t('travelRule.jurisdiction')}>
          <Select value={form.jurisdiction} onChange={e => set("jurisdiction", e.target.value)}>
            {JURISDICTION_THRESHOLDS.map(j => <option key={j.code} value={j.code}>{j.code} — {j.name.split("(")[0].trim()}</option>)}
          </Select>
        </Field>
        <Field label={t('travelRule.registrationNo')}><Input value={form.regNo} onChange={e => set("regNo", e.target.value)} placeholder={t('travelRule.registrationNoPlaceholder')} /></Field>
        <Field label={t('travelRule.trProtocol')}>
          <Select value={form.protocol} onChange={e => set("protocol", e.target.value)}>
            {PROTOCOLS.map(p => <option key={p}>{p}</option>)}
          </Select>
        </Field>
        <Field label={t('travelRule.complianceStatus')}>
          <Select value={form.status} onChange={e => set("status", e.target.value)}>
            {Object.entries(VASP_STATUS_META).map(([k]) => <option key={k} value={k}>{getTranslatedVASPStatus(k, t)}</option>)}
          </Select>
        </Field>
        <Field label={t('travelRule.website')}><Input value={form.website} onChange={e => set("website", e.target.value)} placeholder="example.com" /></Field>
      </div>
      <Field label={t('travelRule.complianceContact')}><Input value={form.contact} onChange={e => set("contact", e.target.value)} placeholder="compliance@example.com" /></Field>

      <Field label={t('travelRule.knownWalletAddresses')}>
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <Input value={addrInput} onChange={e => setAddrInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addAddr()}
            placeholder="0x… or bc1… then press Enter" />
          <Btn small onClick={addAddr}>{t('common.add')}</Btn>
        </div>
        {(form.addresses || []).map((a, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "rgba(0,0,0,0.2)", borderRadius: 5, padding: "5px 10px", marginBottom: 4 }}>
            <span style={{ color: C.textMid, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>{truncate(a, 36)}</span>
            <button onClick={() => removeAddr(i)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 14 }}>×</button>
          </div>
        ))}
      </Field>

      <Field label={t('common.notes')}><Textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder={t('travelRule.vaspNotesPlaceholder')} /></Field>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
        <Btn variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
        <Btn onClick={save} disabled={!form.name}>{t('travelRule.saveVasp')}</Btn>
      </div>
    </Modal>
  );
}

function VASPDirectory({ vasps, setVasps }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [modal, setModal]   = useState(null); // null | "add" | vasp object
  const [selected, setSelected] = useState(null);

  function saveVASP(vasp) {
    setVasps(prev => {
      const idx = prev.findIndex(v => v.id === vasp.id);
      if (idx >= 0) return prev.map(v => v.id === vasp.id ? vasp : v);
      return [vasp, ...prev];
    });
  }
  function deleteVASP(id) { setVasps(p => p.filter(v => v.id !== id)); setSelected(null); }

  const filtered = vasps.filter(v =>
    !search || v.name.toLowerCase().includes(search.toLowerCase())
      || v.jurisdiction.toLowerCase().includes(search.toLowerCase())
      || (v.protocol || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('travelRule.vaspSearchPlaceholder')} />
        <Btn onClick={() => setModal("add")}>{t('travelRule.addVaspBtn')}</Btn>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {[t('travelRule.colVaspName'), t('travelRule.colJurisdiction'), t('travelRule.colProtocol'), t('travelRule.colStatus'), t('travelRule.colRegistration'), t('travelRule.colContact')].map(h => (
                <th key={h} style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em",
                  padding: "6px 10px", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(v => {
              const meta = VASP_STATUS_META[v.status] || VASP_STATUS_META.unknown;
              return (
                <tr key={v.id}
                  onClick={() => setSelected(selected?.id === v.id ? null : v)}
                  style={{ borderBottom: `1px solid ${C.border}`, cursor: "pointer",
                    background: selected?.id === v.id ? C.accentGlow : "transparent",
                    transition: "background 0.1s" }}>
                  <td style={{ padding: "10px 10px", color: C.text, fontWeight: 600 }}>{v.name}</td>
                  <td style={{ padding: "10px 10px", color: C.textMid }}>{v.jurisdiction}</td>
                  <td style={{ padding: "10px 10px", color: C.accent }}>{v.protocol}</td>
                  <td style={{ padding: "10px 10px" }}><Badge label={getTranslatedVASPStatus(v.status, t)} color={meta.color} /></td>
                  <td style={{ padding: "10px 10px", color: C.textDim, fontSize: 11 }}>{v.regNo || "—"}</td>
                  <td style={{ padding: "10px 10px", color: C.textDim, fontSize: 11 }}>{v.contact || "—"}</td>
                  <td style={{ padding: "10px 6px" }}>
                    <Btn small variant="ghost" onClick={e => { e.stopPropagation(); setModal(v); }}>{t('common.edit')}</Btn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div style={{ padding: "32px", textAlign: "center", color: C.textDim, fontSize: 13 }}>{t('travelRule.noVaspsFound')}</div>
        )}
      </div>

      {selected && (
        <div style={{ marginTop: 16, background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "16px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>{selected.name} — Detail</div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn small variant="subtle" onClick={() => setModal(selected)}>{t('common.edit')}</Btn>
              <Btn small variant="danger" onClick={() => deleteVASP(selected.id)}>{t('common.delete')}</Btn>
            </div>
          </div>
          {selected.notes && <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.7, marginBottom: 12 }}>{selected.notes}</div>}
          {(selected.addresses || []).length > 0 && (
            <div>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>{t('travelRule.knownWalletAddresses')}</div>
              {selected.addresses.map((a, i) => (
                <div key={i} style={{ color: C.textMid, fontSize: 11, fontFamily: "'DM Mono',monospace",
                  background: "rgba(0,0,0,0.2)", borderRadius: 5, padding: "4px 8px", marginBottom: 4 }}>{a}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {modal && (
        <VASPModal
          vasp={modal === "add" ? null : modal}
          onClose={() => setModal(null)}
          onSave={v => { saveVASP(v); if (selected?.id === v.id) setSelected(v); }}
        />
      )}
    </div>
  );
}

// ─── TAB 3: THRESHOLD SETTINGS ────────────────────────────────────────────────

function ThresholdSettings({ thresholds, setThresholds }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [editing, setEditing] = useState(null); // jurisdiction code

  function update(code, field, value) {
    setThresholds(prev => prev.map(t => t.code === code ? { ...t, [field]: value } : t));
  }

  return (
    <div>
      <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.7, marginBottom: 20,
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px" }}>
        {t('travelRule.thresholdInstructions')}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {thresholds.map(jur => (
          <div key={jur.code}
            style={{ background: jur.enabled ? C.accentGlow : C.surface,
              border: `1px solid ${jur.enabled ? C.accent + "55" : C.border}`,
              borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div>
                <div style={{ color: C.text, fontSize: 12, fontWeight: 700 }}>{jur.code}</div>
                <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{jur.name}</div>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={jur.enabled}
                  onChange={e => update(jur.code, "enabled", e.target.checked)} />
                <span style={{ color: C.textDim, fontSize: 10 }}>{t('common.active')}</span>
              </label>
            </div>

            {editing === jur.code ? (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <Input type="number" value={jur.threshold}
                  onChange={e => update(jur.code, "threshold", parseFloat(e.target.value) || 0)}
                  style={{ width: 120 }} />
                <span style={{ color: C.textDim, fontSize: 11 }}>{jur.currency}</span>
                <Btn small onClick={() => setEditing(null)}>{t('common.done')}</Btn>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: jur.enabled ? C.accent : C.textMid, fontSize: 18, fontWeight: 700, fontFamily: "'DM Mono',monospace" }}>
                  {jur.threshold === 0 ? t('travelRule.allTransfers') : `${jur.symbol}${jur.threshold.toLocaleString()}`}
                </span>
                {jur.threshold !== 0 && <span style={{ color: C.textDim, fontSize: 11 }}>{jur.currency}</span>}
                <Btn small variant="ghost" onClick={() => setEditing(jur.code)} style={{ marginLeft: "auto" }}>{t('common.edit')}</Btn>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── TAB 4: UNHOSTED WALLET WORKFLOW ─────────────────────────────────────────

const UHW_STEPS = [
  "Transaction Details",
  "Customer Identity",
  "Wallet Ownership",
  "Source of Funds",
  "Risk Assessment",
  "Documentation",
];

function UnhostedWalletWorkflow({ assessments, setAssessments }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [showNew, setShowNew]   = useState(false);
  const [viewing, setViewing]   = useState(null);

  function saveAssessment(a) { setAssessments(p => [a, ...p]); setShowNew(false); }

  if (showNew) return <UHWizard onSave={saveAssessment} onCancel={() => setShowNew(false)} />;
  if (viewing) return <UHDetail assessment={viewing} onBack={() => setViewing(null)}
    onDelete={id => { setAssessments(p => p.filter(a => a.id !== id)); setViewing(null); }} />;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{t('travelRule.unhostedAssessmentsTitle')}</div>
          <div style={{ color: C.textDim, fontSize: 12 }}>
            {t('travelRule.unhostedAssessmentsDesc')}
          </div>
        </div>
        <Btn onClick={() => setShowNew(true)}>{t('travelRule.newAssessmentBtn')}</Btn>
      </div>

      {assessments.length === 0 && (
        <div style={{ padding: "40px 20px", textAlign: "center", color: C.textDim, fontSize: 13,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          {t('travelRule.noAssessments')}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {assessments.map(a => {
          const riskColor = { High: C.danger, Medium: C.warn, Low: C.success }[a.riskLevel] || C.textDim;
          return (
            <div key={a.id} onClick={() => setViewing(a)} style={{ background: C.surface,
              border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px",
              cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>{a.customerName}</div>
                <div style={{ color: C.textDim, fontSize: 11, marginTop: 3 }}>
                  {fmtDate(a.date)} · {a.asset} {a.amount} · {truncate(a.walletAddress, 24)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Badge label={a.riskLevel || t('travelRule.unrated')} color={riskColor} />
                <Badge label={a.decision || t('travelRule.statusPending')} color={a.decision === "Approved" ? C.success : a.decision === "Rejected" ? C.danger : C.warn} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UHWizard({ onSave, onCancel }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    id: uid(), date: new Date().toISOString().slice(0, 10),
    customerName: "", customerId: "", asset: "BTC", amount: "", amountUSD: "",
    direction: "outgoing", walletAddress: "", ownershipProof: "self_declaration",
    ownershipNotes: "", sourceFunds: "", sourceFundsDoc: "none",
    purposeOfTx: "", riskLevel: "Medium", riskRationale: "",
    decision: "Pending", decisionNotes: "",
    checklist: {
      identityVerified: false, walletOwnershipConfirmed: false,
      sourceOfFundsDocumented: false, transactionPurposeRecorded: false,
      sarFiled: false, supervisorReviewed: false,
    },
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setCheck = (k, v) => setForm(f => ({ ...f, checklist: { ...f.checklist, [k]: v } }));

  const stepContent = [
    // Step 0 — Transaction Details
    <div key={0} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
      <Field label={t('travelRule.date')}><Input type="date" value={form.date} onChange={e => set("date", e.target.value)} /></Field>
      <Field label={t('travelRule.direction')}>
        <Select value={form.direction} onChange={e => set("direction", e.target.value)}>
          <option value="outgoing">{t('travelRule.outgoingUnhosted')}</option>
          <option value="incoming">{t('travelRule.incomingUnhosted')}</option>
        </Select>
      </Field>
      <Field label={t('travelRule.asset')}>
        <Select value={form.asset} onChange={e => set("asset", e.target.value)}>
          {ASSETS.map(a => <option key={a}>{a}</option>)}
        </Select>
      </Field>
      <Field label={t('travelRule.amountCrypto')}><Input value={form.amount} onChange={e => set("amount", e.target.value)} placeholder="e.g. 1.2" /></Field>
      <Field label={t('travelRule.amountUSD')}><Input value={form.amountUSD} onChange={e => set("amountUSD", e.target.value)} placeholder="e.g. 45000" /></Field>
      <Field label={t('travelRule.walletAddress')}><Input value={form.walletAddress} onChange={e => set("walletAddress", e.target.value)} placeholder="0x… or bc1…" /></Field>
    </div>,

    // Step 1 — Customer Identity
    <div key={1}>
      <Field label={t('travelRule.customerLegalName')}><Input value={form.customerName} onChange={e => set("customerName", e.target.value)} placeholder={t('travelRule.legalNamePlaceholder')} /></Field>
      <Field label={t('travelRule.customerIdRef')}><Input value={form.customerId} onChange={e => set("customerId", e.target.value)} placeholder={t('travelRule.customerIdPlaceholder')} /></Field>
      <div style={{ background: "rgba(0,212,255,0.06)", border: "1px solid rgba(0,212,255,0.2)", borderRadius: 7, padding: "12px 14px", marginTop: 8 }}>
        <div style={{ color: C.accent, fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{t('travelRule.identityRequirements')}</div>
        <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.7 }}>
          {t('travelRule.identityRequirementsDesc')}
        </div>
      </div>
    </div>,

    // Step 2 — Wallet Ownership
    <div key={2}>
      <Field label={t('travelRule.ownershipProofMethod')}>
        <Select value={form.ownershipProof} onChange={e => set("ownershipProof", e.target.value)}>
          <option value="self_declaration">{t('travelRule.proofSelfDeclaration')}</option>
          <option value="micro_transaction">{t('travelRule.proofMicroTransaction')}</option>
          <option value="cryptographic_signature">{t('travelRule.proofCryptographicSignature')}</option>
          <option value="screenshot">{t('travelRule.proofScreenshot')}</option>
          <option value="unable">{t('travelRule.proofUnable')}</option>
        </Select>
      </Field>
      <Field label={t('travelRule.notesEvidenceRef')}>
        <Textarea value={form.ownershipNotes} onChange={e => set("ownershipNotes", e.target.value)}
          placeholder={t('travelRule.ownershipNotesPlaceholder')} />
      </Field>
      {form.ownershipProof === "unable" && (
        <div style={{ background: "rgba(255,68,68,0.08)", border: "1px solid rgba(255,68,68,0.3)",
          borderRadius: 7, padding: "10px 14px", marginTop: 4 }}>
          <div style={{ color: C.danger, fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{t('travelRule.highRiskUnconfirmed')}</div>
          <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6 }}>
            {t('travelRule.highRiskUnconfirmedDesc')}
          </div>
        </div>
      )}
    </div>,

    // Step 3 — Source of Funds
    <div key={3}>
      <Field label={t('travelRule.sourceFundsDescription')}>
        <Textarea value={form.sourceFunds} onChange={e => set("sourceFunds", e.target.value)} rows={4}
          placeholder={t('travelRule.sourceFundsPlaceholder')} />
      </Field>
      <Field label={t('travelRule.supportingDocumentation')}>
        <Select value={form.sourceFundsDoc} onChange={e => set("sourceFundsDoc", e.target.value)}>
          <option value="none">{t('travelRule.docNone')}</option>
          <option value="bank_statement">{t('travelRule.docBankStatement')}</option>
          <option value="payslip">{t('travelRule.docPayslip')}</option>
          <option value="tax_return">{t('travelRule.docTaxReturn')}</option>
          <option value="business_financials">{t('travelRule.docBusinessFinancials')}</option>
          <option value="exchange_history">{t('travelRule.docExchangeHistory')}</option>
          <option value="sale_contract">{t('travelRule.docSaleContract')}</option>
          <option value="other">{t('travelRule.docOther')}</option>
        </Select>
      </Field>
      <Field label={t('travelRule.transactionPurpose')}>
        <Textarea value={form.purposeOfTx} onChange={e => set("purposeOfTx", e.target.value)} rows={2}
          placeholder={t('travelRule.transactionPurposePlaceholder')} />
      </Field>
    </div>,

    // Step 4 — Risk Assessment
    <div key={4}>
      <Field label={t('travelRule.overallRiskLevel')}>
        <Select value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}>
          <option value="Low">{t('common.low')}</option>
          <option value="Medium">{t('common.medium')}</option>
          <option value="High">{t('common.high')}</option>
        </Select>
      </Field>
      <Field label={t('travelRule.riskRationale')}>
        <Textarea value={form.riskRationale} onChange={e => set("riskRationale", e.target.value)} rows={4}
          placeholder={t('travelRule.riskRationalePlaceholder')} />
      </Field>
      <Field label={t('travelRule.decision')}>
        <Select value={form.decision} onChange={e => set("decision", e.target.value)}>
          <option value="Pending">{t('travelRule.decisionPending')}</option>
          <option value="Approved">{t('travelRule.decisionApproved')}</option>
          <option value="Approved with Conditions">{t('travelRule.decisionApprovedConditions')}</option>
          <option value="Rejected">{t('travelRule.decisionRejected')}</option>
          <option value="Escalated">{t('travelRule.decisionEscalated')}</option>
        </Select>
      </Field>
      <Field label={t('travelRule.decisionNotes')}>
        <Textarea value={form.decisionNotes} onChange={e => set("decisionNotes", e.target.value)} rows={2}
          placeholder={t('travelRule.decisionNotesPlaceholder')} />
      </Field>
    </div>,

    // Step 5 — Documentation Checklist
    <div key={5}>
      <div style={{ color: C.textMid, fontSize: 12, marginBottom: 16 }}>
        {t('travelRule.checklistIntro')}
      </div>
      {[
        ["identityVerified",             t('travelRule.checkIdentityVerified')],
        ["walletOwnershipConfirmed",      t('travelRule.checkWalletOwnership')],
        ["sourceOfFundsDocumented",       t('travelRule.checkSourceOfFunds')],
        ["transactionPurposeRecorded",    t('travelRule.checkTxPurpose')],
        ["sarFiled",                      t('travelRule.checkSarFiled')],
        ["supervisorReviewed",            t('travelRule.checkSupervisorReviewed')],
      ].map(([key, label]) => (
        <label key={key} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={form.checklist[key]}
            onChange={e => setCheck(key, e.target.checked)}
            style={{ marginTop: 2, flexShrink: 0 }} />
          <span style={{ color: form.checklist[key] ? C.success : C.textMid, fontSize: 12, lineHeight: 1.5 }}>{label}</span>
        </label>
      ))}
      <div style={{ background: "rgba(0,0,0,0.2)", border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px", marginTop: 8 }}>
        <div style={{ color: C.textDim, fontSize: 11, marginBottom: 6, fontWeight: 700 }}>{t('common.summary')}</div>
        <DetailRow label={t('travelRule.customer')}    value={form.customerName || "—"} C={{ textDim: C.textDim, textMid: C.textMid }} />
        <DetailRow label={t('travelRule.walletAddress')} value={truncate(form.walletAddress, 28)} C={{ textDim: C.textDim, textMid: C.textMid }} />
        <DetailRow label={t('travelRule.amount')}     value={`${form.amount} ${form.asset} (~$${parseFloat(form.amountUSD || 0).toLocaleString()})`} C={{ textDim: C.textDim, textMid: C.textMid }} />
        <DetailRow label={t('travelRule.riskLevel')}  value={form.riskLevel} C={{ textDim: C.textDim, textMid: C.textMid }} />
        <DetailRow label={t('travelRule.decision')}   value={form.decision} C={{ textDim: C.textDim, textMid: C.textMid }} />
      </div>
    </div>,
  ];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
        <button onClick={onCancel} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 13 }}>← {t('common.back')}</button>
        <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{t('travelRule.newAssessmentTitle')}</div>
      </div>

      {/* Step progress */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, overflowX: "auto" }}>
        {UHW_STEPS.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 12px",
              background: i === step ? C.accentGlow : "transparent",
              border: `1px solid ${i === step ? C.accent : i < step ? C.success + "55" : C.border}`,
              borderRadius: 6, cursor: i <= step ? "pointer" : "default" }}
              onClick={() => i <= step && setStep(i)}>
              <div style={{ width: 18, height: 18, borderRadius: "50%",
                background: i < step ? C.success : i === step ? C.accent : C.border,
                color: i < step || i === step ? C.bg : C.textDim,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                {i < step ? "✓" : i + 1}
              </div>
              <span style={{ color: i === step ? C.accent : i < step ? C.success : C.textDim, fontSize: 11 }}>{s}</span>
            </div>
            {i < UHW_STEPS.length - 1 && <div style={{ width: 20, height: 1, background: C.border }} />}
          </div>
        ))}
      </div>

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "20px 22px", marginBottom: 16 }}>
        <div style={{ color: C.textDim, fontSize: 11, letterSpacing: "0.08em", marginBottom: 16, fontWeight: 700 }}>
          {t('travelRule.stepOf', { step: step + 1, title: UHW_STEPS[step].toUpperCase() })}
        </div>
        {stepContent[step]}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Btn variant="ghost" onClick={() => step > 0 ? setStep(s => s - 1) : onCancel()}>
          {step === 0 ? t('common.cancel') : `← ${t('common.previous')}`}
        </Btn>
        {step < UHW_STEPS.length - 1
          ? <Btn onClick={() => setStep(s => s + 1)}>{t('common.next')} →</Btn>
          : <Btn onClick={() => onSave(form)}>{t('travelRule.saveAssessment')}</Btn>
        }
      </div>
    </div>
  );
}

function UHDetail({ assessment: a, onBack, onDelete }) {
  const C = useTheme();
  const { t } = useTranslation();
  const riskColor = { High: C.danger, Medium: C.warn, Low: C.success }[a.riskLevel] || C.textDim;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 13 }}>← {t('common.back')}</button>
        <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{t('travelRule.unhostedAssessmentDetail')}</div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Badge label={a.riskLevel} color={riskColor} />
          <Badge label={a.decision} color={a.decision === "Approved" ? C.success : a.decision === "Rejected" ? C.danger : C.warn} />
          <Btn small variant="danger" onClick={() => onDelete(a.id)}>{t('common.delete')}</Btn>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Section title={t('travelRule.sectionTransaction')} C={C}>
          <DetailRow label={t('travelRule.date')}      value={fmtDate(a.date)} C={C} />
          <DetailRow label={t('travelRule.customer')}  value={a.customerName} C={C} />
          <DetailRow label={t('travelRule.customerId')} value={a.customerId || "—"} C={C} />
          <DetailRow label={t('travelRule.direction')} value={a.direction} C={C} />
          <DetailRow label={t('travelRule.asset')}     value={`${a.amount} ${a.asset}`} C={C} />
          <DetailRow label={t('travelRule.usdValue')} value={`$${parseFloat(a.amountUSD || 0).toLocaleString()}`} C={C} />
          <DetailRow label={t('travelRule.walletAddress')} value={truncate(a.walletAddress, 28)} C={C} />
        </Section>

        <Section title={t('travelRule.sectionOwnershipFunds')} C={C}>
          <DetailRow label={t('travelRule.ownershipProof')} value={a.ownershipProof?.replace(/_/g, " ")} C={C} />
          {a.ownershipNotes && <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6, marginBottom: 6 }}>{a.ownershipNotes}</div>}
          <DetailRow label={t('travelRule.sourceFundsDoc')} value={a.sourceFundsDoc?.replace(/_/g, " ")} C={C} />
          {a.sourceFunds && <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6, marginBottom: 6 }}>{a.sourceFunds}</div>}
          {a.purposeOfTx && <><DetailRow label={t('travelRule.purpose')} value="" C={C} /><div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6 }}>{a.purposeOfTx}</div></>}
        </Section>

        <Section title={t('travelRule.sectionRiskDecision')} C={C}>
          <DetailRow label={t('travelRule.riskLevel')} value={a.riskLevel} C={C} />
          {a.riskRationale && <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6, marginBottom: 6 }}>{a.riskRationale}</div>}
          <DetailRow label={t('travelRule.decision')} value={a.decision} C={C} />
          {a.decisionNotes && <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6 }}>{a.decisionNotes}</div>}
        </Section>

        <Section title={t('travelRule.sectionDocChecklist')} C={C}>
          {Object.entries(a.checklist || {}).map(([k, v]) => (
            <div key={k} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 7 }}>
              <span style={{ color: v ? C.success : C.danger, fontSize: 14 }}>{v ? "✓" : "✗"}</span>
              <span style={{ color: v ? C.textMid : C.textDim, fontSize: 11 }}>{k.replace(/([A-Z])/g, " $1").trim()}</span>
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children, C }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", fontWeight: 700, marginBottom: 12 }}>{title.toUpperCase()}</div>
      {children}
    </div>
  );
}

// ─── TAB 5: CASE FILES ────────────────────────────────────────────────────────

function CaseFiles({ caseFiles, setCaseFiles, transactions }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [showNew, setShowNew] = useState(false);
  const [viewing, setViewing] = useState(null);

  function saveCase(cf) { setCaseFiles(p => [cf, ...p]); setShowNew(false); }
  function deleteCase(id) { setCaseFiles(p => p.filter(c => c.id !== id)); setViewing(null); }

  if (showNew) return <CaseFileForm onSave={saveCase} onCancel={() => setShowNew(false)} transactions={transactions} />;
  if (viewing) return <CaseFileDetail cf={viewing} onBack={() => setViewing(null)} onDelete={deleteCase} />;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{t('travelRule.caseFilesTitle')}</div>
          <div style={{ color: C.textDim, fontSize: 12 }}>
            {t('travelRule.caseFilesDesc')}
          </div>
        </div>
        <Btn onClick={() => setShowNew(true)}>{t('travelRule.newCaseFileBtn')}</Btn>
      </div>

      {caseFiles.length === 0 && (
        <div style={{ padding: "40px 20px", textAlign: "center", color: C.textDim, fontSize: 13,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          {t('travelRule.noCaseFiles')}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {caseFiles.map(cf => {
          const txStatus = cf.transmissionStatus;
          const txColor  = txStatus === "Sent" ? C.success : txStatus === "Failed" ? C.danger : C.warn;
          return (
            <div key={cf.id} onClick={() => setViewing(cf)}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
                padding: "12px 16px", cursor: "pointer", display: "flex",
                justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>
                  {cf.subjectName} — {cf.asset} {cf.amount}
                </div>
                <div style={{ color: C.textDim, fontSize: 11, marginTop: 3 }}>
                  {fmtDate(cf.date)} · Ref: {cf.ref || cf.id.slice(-8)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Badge label={cf.originatorCollected ? `${t('travelRule.originator')} ✓` : `${t('travelRule.originator')} ✗`}
                  color={cf.originatorCollected ? C.success : C.danger} />
                <Badge label={cf.beneficiaryCollected ? `${t('travelRule.beneficiary')} ✓` : `${t('travelRule.beneficiary')} ✗`}
                  color={cf.beneficiaryCollected ? C.success : C.danger} />
                <Badge label={txStatus || t('travelRule.statusPending')} color={txColor} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CaseFileForm({ onSave, onCancel, transactions }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    id: uid(), date: new Date().toISOString().slice(0, 10),
    ref: "", linkedTxId: "",
    subjectName: "", asset: "BTC", amount: "", amountUSD: "", direction: "outgoing",
    originatorCollected: false, originatorMethod: "",
    beneficiaryCollected: false, beneficiaryMethod: "",
    transmissionStatus: "Pending", transmissionMethod: "", transmissionRef: "",
    sunriseProblem: false, sunriseNotes: "",
    complianceOfficer: "", notes: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function linkTx(id) {
    const tx = transactions.find(t => t.id === id);
    if (!tx) { set("linkedTxId", id); return; }
    setForm(f => ({ ...f, linkedTxId: id, subjectName: tx.originatorName || f.subjectName,
      asset: tx.asset, amount: tx.amountCrypto, amountUSD: tx.amountUSD,
      direction: tx.direction }));
  }

  function save() {
    if (!form.subjectName || !form.amount) return;
    onSave(form);
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
        <button onClick={onCancel} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 13 }}>← {t('common.back')}</button>
        <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{t('travelRule.newCaseFileTitle')}</div>
      </div>

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "20px 22px", marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          <Field label={t('travelRule.date')}><Input type="date" value={form.date} onChange={e => set("date", e.target.value)} /></Field>
          <Field label={t('travelRule.internalReference')}><Input value={form.ref} onChange={e => set("ref", e.target.value)} placeholder="e.g. TR-2025-001" /></Field>

          {transactions.length > 0 && (
            <Field label={t('travelRule.linkToTransaction')}>
              <Select value={form.linkedTxId} onChange={e => linkTx(e.target.value)}>
                <option value="">— {t('travelRule.selectTransaction')} —</option>
                {transactions.map(tx => (
                  <option key={tx.id} value={tx.id}>
                    {fmtDate(tx.date)} · {tx.asset} {tx.amountCrypto} · {tx.originatorName} → {tx.beneficiaryName}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field label={t('travelRule.subjectName')}><Input value={form.subjectName} onChange={e => set("subjectName", e.target.value)} placeholder={t('travelRule.subjectNamePlaceholder')} /></Field>
          <Field label={t('travelRule.asset')}>
            <Select value={form.asset} onChange={e => set("asset", e.target.value)}>
              {ASSETS.map(a => <option key={a}>{a}</option>)}
            </Select>
          </Field>
          <Field label={t('travelRule.amountCrypto')}><Input value={form.amount} onChange={e => set("amount", e.target.value)} placeholder="0.0" /></Field>
          <Field label={t('travelRule.amountUSDField')}><Input value={form.amountUSD} onChange={e => set("amountUSD", e.target.value)} placeholder="0" /></Field>
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, margin: "16px 0" }} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
          <div>
            <div style={{ color: C.accent, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 10 }}>{t('travelRule.originatorInfo')}</div>
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={form.originatorCollected} onChange={e => set("originatorCollected", e.target.checked)} />
              <span style={{ color: C.textMid, fontSize: 12 }}>{t('travelRule.informationCollected')}</span>
            </label>
            <Field label={t('travelRule.collectionMethod')}>
              <Input value={form.originatorMethod} onChange={e => set("originatorMethod", e.target.value)}
                placeholder={t('travelRule.originatorMethodPlaceholder')} />
            </Field>
          </div>
          <div>
            <div style={{ color: C.purple, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 10 }}>{t('travelRule.beneficiaryInfo')}</div>
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={form.beneficiaryCollected} onChange={e => set("beneficiaryCollected", e.target.checked)} />
              <span style={{ color: C.textMid, fontSize: 12 }}>{t('travelRule.informationCollected')}</span>
            </label>
            <Field label={t('travelRule.collectionMethod')}>
              <Input value={form.beneficiaryMethod} onChange={e => set("beneficiaryMethod", e.target.value)}
                placeholder={t('travelRule.beneficiaryMethodPlaceholder')} />
            </Field>
          </div>
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, margin: "16px 0" }} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
          <Field label={t('travelRule.transmissionStatus')}>
            <Select value={form.transmissionStatus} onChange={e => set("transmissionStatus", e.target.value)}>
              {["Pending", "Sent", "Received", "Failed", "N/A — Exempt"].map(s => <option key={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label={t('travelRule.transmissionMethod')}>
            <Input value={form.transmissionMethod} onChange={e => set("transmissionMethod", e.target.value)}
              placeholder="TRISA, Notabene, email, etc." />
          </Field>
          <Field label={t('travelRule.transmissionRef')}>
            <Input value={form.transmissionRef} onChange={e => set("transmissionRef", e.target.value)}
              placeholder={t('travelRule.transmissionRefPlaceholder')} />
          </Field>
        </div>

        <div style={{ marginTop: 8 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, cursor: "pointer" }}>
            <input type="checkbox" checked={form.sunriseProblem} onChange={e => set("sunriseProblem", e.target.checked)} />
            <span style={{ color: C.textMid, fontSize: 12 }}>{t('travelRule.sunriseProblem')}</span>
          </label>
          {form.sunriseProblem && (
            <Field label={t('travelRule.sunriseProblemNotes')}>
              <Textarea value={form.sunriseNotes} onChange={e => set("sunriseNotes", e.target.value)}
                placeholder={t('travelRule.sunriseNotesPlaceholder')} />
            </Field>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px", marginTop: 4 }}>
          <Field label={t('travelRule.complianceOfficer')}>
            <Input value={form.complianceOfficer} onChange={e => set("complianceOfficer", e.target.value)}
              placeholder={t('travelRule.complianceOfficerPlaceholder')} />
          </Field>
        </div>
        <Field label={t('common.notes')}>
          <Textarea value={form.notes} onChange={e => set("notes", e.target.value)}
            placeholder={t('travelRule.caseFileNotesPlaceholder')} />
        </Field>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <Btn variant="ghost" onClick={onCancel}>{t('common.cancel')}</Btn>
        <Btn onClick={save} disabled={!form.subjectName || !form.amount}>{t('travelRule.saveCaseFile')}</Btn>
      </div>
    </div>
  );
}

function CaseFileDetail({ cf, onBack, onDelete }) {
  const C = useTheme();
  const { t } = useTranslation();
  const txColor = cf.transmissionStatus === "Sent" ? C.success : cf.transmissionStatus === "Failed" ? C.danger : C.warn;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 13 }}>← {t('common.back')}</button>
        <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{t('travelRule.caseFileDetailTitle')}</div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Badge label={cf.transmissionStatus || t('travelRule.statusPending')} color={txColor} />
          <Btn small variant="danger" onClick={() => onDelete(cf.id)}>{t('common.delete')}</Btn>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Section title={t('travelRule.sectionTransaction')} C={C}>
          <DetailRow label={t('travelRule.reference')}  value={cf.ref || cf.id.slice(-8)} C={C} />
          <DetailRow label={t('travelRule.date')}       value={fmtDate(cf.date)} C={C} />
          <DetailRow label={t('travelRule.subject')}    value={cf.subjectName} C={C} />
          <DetailRow label={t('travelRule.asset')}      value={`${cf.amount} ${cf.asset}`} C={C} />
          <DetailRow label={t('travelRule.usdValue')}   value={`$${parseFloat(cf.amountUSD || 0).toLocaleString()}`} C={C} />
          <DetailRow label={t('travelRule.direction')}  value={cf.direction} C={C} />
        </Section>

        <Section title={t('travelRule.sectionInfoCollection')} C={C}>
          <DetailRow label={t('travelRule.originatorCollected')} value={cf.originatorCollected ? t('common.yes') : t('common.no')} C={C} />
          {cf.originatorMethod && <div style={{ color: C.textMid, fontSize: 11, marginBottom: 8 }}>{cf.originatorMethod}</div>}
          <DetailRow label={t('travelRule.beneficiaryCollected')} value={cf.beneficiaryCollected ? t('common.yes') : t('common.no')} C={C} />
          {cf.beneficiaryMethod && <div style={{ color: C.textMid, fontSize: 11 }}>{cf.beneficiaryMethod}</div>}
        </Section>

        <Section title={t('travelRule.sectionTransmission')} C={C}>
          <DetailRow label={t('common.status')}   value={cf.transmissionStatus} C={C} />
          <DetailRow label={t('travelRule.method')}   value={cf.transmissionMethod || "—"} C={C} />
          <DetailRow label={t('travelRule.ref')}     value={cf.transmissionRef || "—"} C={C} />
          {cf.sunriseProblem && (
            <div style={{ marginTop: 8 }}>
              <div style={{ color: C.warn, fontSize: 11, fontWeight: 700, marginBottom: 4 }}>⚠ {t('travelRule.sunriseProblemLabel')}</div>
              <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6 }}>{cf.sunriseNotes}</div>
            </div>
          )}
        </Section>

        <Section title={t('travelRule.sectionReview')} C={C}>
          <DetailRow label={t('travelRule.complianceOfficer')} value={cf.complianceOfficer || "—"} C={C} />
          {cf.notes && <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6, marginTop: 6 }}>{cf.notes}</div>}
        </Section>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

const TABS = [
  { id: "log",        labelKey: "travelRule.tabLog",          icon: "⇄" },
  { id: "vasp",       labelKey: "travelRule.tabVasp",         icon: "◈" },
  { id: "thresholds", labelKey: "travelRule.tabThresholds",   icon: "◉" },
  { id: "unhosted",   labelKey: "travelRule.tabUnhosted",     icon: "◍" },
  { id: "casefiles",  labelKey: "travelRule.tabCaseFiles",    icon: "◭" },
];

function load(key, fallback) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
}
function save(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// F-023: Supabase table: travel_rule_records (id text PK, company_id uuid, record_type text, data jsonb, updated_at timestamptz)
// CREATE TABLE travel_rule_records (id text PRIMARY KEY, company_id uuid REFERENCES companies(id), record_type text NOT NULL, data jsonb NOT NULL, updated_at timestamptz DEFAULT now());
// ALTER TABLE travel_rule_records ENABLE ROW LEVEL SECURITY;
// CREATE POLICY "company members" ON travel_rule_records USING (company_id IN (SELECT company_id FROM company_members WHERE user_id = auth.uid()));

export default function TravelRulePage({ currentUser, company }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [activeTab, setActiveTab]   = useState("log");
  const [transactions, setTxRaw]    = useState(() => load("tr_transactions", []));
  const [vasps, setVaspsRaw]        = useState(() => load("tr_vasps", SEED_VASPS));
  const [assessments, setAssessRaw] = useState(() => load("tr_unhosted", []));
  const [caseFiles, setCasesRaw]    = useState(() => load("tr_casefiles", []));
  const [thresholds, setThreshRaw]  = useState(() => {
    const saved = load("tr_thresholds", null);
    if (saved) return saved;
    return JURISDICTION_THRESHOLDS.map(j => ({ ...j, enabled: ["FATF", "US", "EU", "GB", "CA"].includes(j.code) }));
  });
  const dbLoaded = useRef(false);

  // F-023: Load from Supabase on mount (falls back to localStorage if table not yet created)
  useEffect(() => {
    if (!company?.id || dbLoaded.current) return;
    dbLoaded.current = true;
    (async () => {
      try {
        const { data: rows, error } = await db.from("travel_rule_records")
          .select("id, record_type, data")
          .eq("company_id", company.id)
          .execute();
        if (error || !rows?.length) return; // table may not exist yet — localStorage fallback stays
        const byType = { transaction: [], unhosted: [], casefile: [] };
        rows.forEach(r => { if (byType[r.record_type]) byType[r.record_type].push(r.data); });
        if (byType.transaction.length) { save("tr_transactions", byType.transaction); setTxRaw(byType.transaction); }
        if (byType.unhosted.length)    { save("tr_unhosted",     byType.unhosted);    setAssessRaw(byType.unhosted); }
        if (byType.casefile.length)    { save("tr_casefiles",    byType.casefile);    setCasesRaw(byType.casefile); }
      } catch (err) {
        console.error("[Travel Rule] DB load error:", err.message);
      }
    })();
  }, [company?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // F-023: Upsert a single record to Neon (fire-and-forget, localStorage is primary)
  async function dbUpsert(record_type, item) {
    if (!company?.id || !item?.id) return;
    const recordId = `${company.id}_${item.id}`;
    try {
      // Try insert first
      await db.from("travel_rule_records").insert({
        id: recordId,
        company_id: company.id,
        record_type,
        data: item,
        updated_at: new Date().toISOString(),
      }).execute();
    } catch (err) {
      // If insert fails (likely duplicate), update instead
      if (err.message?.includes("duplicate") || err.message?.includes("conflict")) {
        try {
          await db.from("travel_rule_records").update({
            data: item,
            updated_at: new Date().toISOString(),
          }).eq("id", recordId).execute();
        } catch (updateErr) {
          console.error("[Travel Rule] DB update error:", updateErr.message);
        }
      } else {
        console.error("[Travel Rule] DB insert error:", err.message);
      }
    }
  }

  async function dbDelete(record_type, itemId) {
    if (!company?.id || !itemId) return;
    await db.from("travel_rule_records")
      .delete()
      .eq("id", `${company.id}_${itemId}`).execute();
  }

  function setTransactions(fn) {
    setTxRaw(p => {
      const n = typeof fn === "function" ? fn(p) : fn;
      save("tr_transactions", n);
      // Upsert added/changed rows — detect by diffing (simplified: upsert all that differ)
      if (Array.isArray(n) && Array.isArray(p)) {
        n.forEach(item => { if (!p.find(x => x.id === item.id && JSON.stringify(x) === JSON.stringify(item))) dbUpsert("transaction", item); });
        p.forEach(item => { if (!n.find(x => x.id === item.id)) dbDelete("transaction", item.id); });
      }
      return n;
    });
  }
  function setVasps(fn)        { setVaspsRaw(p => { const n = typeof fn === "function" ? fn(p) : fn; save("tr_vasps", n); return n; }); }
  function setAssessments(fn)  {
    setAssessRaw(p => {
      const n = typeof fn === "function" ? fn(p) : fn;
      save("tr_unhosted", n);
      if (Array.isArray(n) && Array.isArray(p)) {
        n.forEach(item => { if (!p.find(x => x.id === item.id && JSON.stringify(x) === JSON.stringify(item))) dbUpsert("unhosted", item); });
        p.forEach(item => { if (!n.find(x => x.id === item.id)) dbDelete("unhosted", item.id); });
      }
      return n;
    });
  }
  function setCaseFiles(fn)    {
    setCasesRaw(p => {
      const n = typeof fn === "function" ? fn(p) : fn;
      save("tr_casefiles", n);
      if (Array.isArray(n) && Array.isArray(p)) {
        n.forEach(item => { if (!p.find(x => x.id === item.id && JSON.stringify(x) === JSON.stringify(item))) dbUpsert("casefile", item); });
        p.forEach(item => { if (!n.find(x => x.id === item.id)) dbDelete("casefile", item.id); });
      }
      return n;
    });
  }
  function setThresholds(fn)   { setThreshRaw(p => { const n = typeof fn === "function" ? fn(p) : fn; save("tr_thresholds", n); return n; }); }

  // Stats for header
  const pendingTx   = transactions.filter(t => t.trStatus === "pending" || t.trStatus === "needs_info").length;
  const breachedTx  = transactions.filter(t => t.thresholdBreached).length;
  const unhostedTx  = transactions.filter(t => t.isUnhosted).length;

  const content = {
    log:        <TransactionLog    transactions={transactions} setTransactions={setTransactions} thresholds={thresholds} />,
    vasp:       <VASPDirectory     vasps={vasps} setVasps={setVasps} />,
    thresholds: <ThresholdSettings thresholds={thresholds} setThresholds={setThresholds} />,
    unhosted:   <UnhostedWalletWorkflow assessments={assessments} setAssessments={setAssessments} />,
    casefiles:  <CaseFiles         caseFiles={caseFiles} setCaseFiles={setCaseFiles} transactions={transactions} />,
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, fontFamily: "'Syne',sans-serif", color: C.text }}>
            {t('travelRule.title')}
          </h1>
          <span style={{ color: C.textDim, fontSize: 12 }}>{t('travelRule.fatfRec16')}</span>
        </div>
        <div style={{ color: C.textDim, fontSize: 12, marginBottom: 16 }}>
          {t('travelRule.subtitle')}
        </div>

        {/* Stat pills */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { label: t('travelRule.statTransactionsLogged'),  value: transactions.length, color: C.accent },
            { label: t('travelRule.statThresholdBreaches'),   value: breachedTx,          color: C.warn   },
            { label: t('travelRule.statPendingNeedsInfo'),    value: pendingTx,           color: pendingTx > 0 ? C.danger : C.success },
            { label: t('travelRule.statUnhostedTxs'),         value: unhostedTx,          color: C.purple },
            { label: t('travelRule.statKnownVasps'),          value: vasps.length,        color: C.textMid },
            { label: t('travelRule.statCaseFiles'),           value: caseFiles.length,    color: C.gold   },
          ].map(s => (
            <div key={s.label} style={{ background: C.surface, border: `1px solid ${C.border}`,
              borderRadius: 8, padding: "8px 14px", display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ color: s.color, fontSize: 18, fontWeight: 800, fontFamily: "'DM Mono',monospace" }}>{s.value}</span>
              <span style={{ color: C.textDim, fontSize: 11 }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${C.border}`, marginBottom: 24, overflowX: "auto" }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ padding: "10px 18px", background: "none", border: "none",
              borderBottom: `2px solid ${activeTab === tab.id ? C.accent : "transparent"}`,
              color: activeTab === tab.id ? C.accent : C.textDim,
              cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12,
              whiteSpace: "nowrap", transition: "all 0.12s", display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: 13 }}>{tab.icon}</span>
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {content[activeTab]}
    </div>
  );
}
